import {
    GetEventPartyListUsingGETStatusesEnum,
    JsonEventDayInfo,
    JsonEventPartyDay,
    JsonEventPartyInfo,
    JsonEventPartyInfoPartyTypeEnum,
    JsonEventPartyMassActionRequestActionEnum,
    JsonPlaceInfo
} from "../../generated-api";
import {useAppTranslation} from "../../services/i18n";
import * as React from "react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Card, CardContent, CardHeader, Chip, Grid, LinearProgress, Link, Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";
import {createOptions, FAKE_VALUE_ALL, FAKE_VALUE_EMPTY} from "../../model/form";
import {Link as RouterLink} from "react-router-dom";
import {dateToGuiAs} from "../../helpers/date";
import {useAppDispatch, useAppSelector} from "../../store";
import {CodebookState, FIXED_EVENT_DAY_NOS} from "../../store/codebooks";
import {selectCodebooks} from "../../store/selectors";
import {getApiResult} from "../../helpers/api";
import {fetchPlacesOnline} from "../../store/places";
import {fetchEventPartiesOnline, fetchEventPartyInfo, massEventParty} from "../../store/eventParties";

type ParkPlaceGroup = {
    groupId: number,
    title: string,
    days: { [key in number]: JsonEventPartyInfo[] }
}

type ParkPlace = {
    placeId: number,
    title: string,
    capacity?: number,
    groups: ParkPlaceGroup[]
}

type EventParkPlaceProps = {
    eventId: number,
    pp: ParkPlace,
    days: JsonEventDayInfo[]
}

const h100 = {height: '100%'};

type EventFoodPlaceProps = {
    eventId: number,
    pp: ParkPlace,
    days: JsonEventDayInfo[]
}

const EventParkPlace = (props: EventParkPlaceProps) => {
    const {eventId, pp, days} = props;

    const t = useAppTranslation();

    return useMemo(() => {
        const totals: { [key in number]: number } = {};
        const useds: { [key in number]: number } = {};

        const rows = pp.groups.map((g) => {
            return <TableRow key={g.groupId}>
                <TableCell><span><strong>{g.title}</strong></span></TableCell>
                {days?.map((d) => {
                    if (!d.dayNo) {
                        return null;
                    }
                    const [total, used] = g.days[d.dayNo]
                        ?.map((ep) => {
                            const dd = ep.eventDayDetails?.find((dd) => dd.dayNo === d.dayNo);
                            const total = dd?.parkCnt !== undefined
                                ? dd.parkCnt // can be 0
                                : 1;
                            const used = dd?.parkRec !== undefined
                                ? dd.parkRec // can be 0
                                : 0;
                            return [total, used];
                        })
                        ?.reduce(([t, u], [st, su]) => [t + st, u + su], [0, 0]) || [0, 0];
                    if (!totals[d.dayNo]) {
                        totals[d.dayNo] = 0;
                        useds[d.dayNo] = 0;
                    }
                    totals[d.dayNo] += total;
                    useds[d.dayNo] += used;

                    return <TableCell key={d.dayNo} style={{textAlign: 'center'}}><span>{!!total && <Link underline={'hover'}
                        color={total === used ? 'var(--color-success)' : undefined}
                        to={`/event-parties/?eventId=${eventId}&eventDay=${d.dayNo}&groupIds=${g.groupId}&parkIds=${pp.placeId}`}
                        component={RouterLink}>{total}</Link>} {!!used ? (used === total ? null : used) : null}</span>
                    </TableCell>;
                })}
            </TableRow>
        });
        if (rows.length <= 0) {
            return null;
        }
        if (rows.length > 1) {
            rows.push(<TableRow key={'total'}>
                <TableCell><span><strong>{t('Celkem')}</strong></span></TableCell>
                {days?.map((d) => {
                    if (!d.dayNo) {
                        return null;
                    }
                    const k = d.dayNo;
                    const total = totals[k] || 0;
                    const used = useds[k] || 0;
                    return <TableCell key={d.dayNo} style={{textAlign: 'center'}}><span><strong>{total || 0}</strong><br/>{used}</span>
                    </TableCell>;
                })}
            </TableRow>);
        }

        const isWide = days.length > 6;
        return <Grid key={pp.title} item md={6} lg={isWide ? 6 : 4}>
            <Card sx={h100}>
                <CardHeader title={<span>{pp.title}<span style={{float: 'right'}}>{pp.capacity}</span></span>}/>
                <CardContent>
                    <Table className={'data-grid'}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{width: isWide ? '20%' : '40%'}}><span>{t('Dny')}</span></TableCell>
                                {days?.map((d) => {
                                    return <TableCell key={d.dayNo} style={{width: ((isWide ? 80 : 60) / days.length) + '%', textAlign: 'center'}}>
                                        <span>{dateToGuiAs(d.dayDate, "eeeee d.M.")}</span>
                                    </TableCell>;
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Grid>
    }, [eventId, pp, days, t]);
}

const EventReportParking = ({eventId, eventDays}: { eventId: number, eventDays: JsonEventDayInfo[] }) => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const codebooks = useAppSelector<CodebookState>(selectCodebooks);
    const placeParkList = codebooks['placePark'];
    const groupList = codebooks['group'];

    const [eventParties, setEventParties] = useState<JsonEventPartyInfo[] | undefined>(undefined);
    const [techParties, setTechParties] = useState<JsonEventPartyInfo[] | undefined>(undefined);
    const [placeInfos, setPlaceInfos] = useState<JsonPlaceInfo[] | undefined>(undefined);

    const handleFetchItems = useCallback(async () => {
        setPlaceInfos(getApiResult(await dispatch(fetchPlacesOnline({eventId}))));
        const eventParties = getApiResult<JsonEventPartyInfo[]>(await dispatch(fetchEventPartiesOnline({
            orderCol: 'fullName',
            rows: 10000,
            eventId,
            parkIds: [FAKE_VALUE_ALL],
            statuses: [GetEventPartyListUsingGETStatusesEnum.Pending, GetEventPartyListUsingGETStatusesEnum.Active]
        })));

        setEventParties(eventParties);
        setTechParties(eventParties?.filter((ep) => ep.partyType === JsonEventPartyInfoPartyTypeEnum.T));

    }, [eventId, dispatch]);

    const handleAddParking = useCallback(async (ep: JsonEventPartyInfo, dayNo: number) => {
        if (!ep.eventPartyId) {
            return;
        }

        const eventParty = getApiResult<JsonEventPartyInfo>(await dispatch(fetchEventPartyInfo(ep.eventPartyId)));
        if (eventParty?.eventPartyId) {
            const eventPartyDays: JsonEventPartyDay[] = Array.from({length: dayNo}, () => ({} as JsonEventPartyDay));
            eventPartyDays[dayNo - 1] = {
                parkCnt: (eventParty.eventDayDetails?.find((dd) => dd.dayNo === dayNo)?.parkCnt || 1) + 1,
                parkRec: (eventParty.eventDayDetails?.find((dd) => dd.dayNo === dayNo)?.parkRec || 1) + 1,
            };
            await dispatch(massEventParty({
                request: {
                    groupId: FAKE_VALUE_EMPTY,
                    action: JsonEventPartyMassActionRequestActionEnum.Update,
                    eventId: eventParty.eventId,
                    items: [{eventPartyId: eventParty?.eventPartyId}],
                    eventPartyDays
                }
            }));
            await handleFetchItems();
        }

    }, [handleFetchItems, dispatch]);

    const parkPlaces = useMemo(() => {
        const parkPlaces: ParkPlace[] = [];
        if (!eventParties || !placeParkList || !groupList || !eventDays || !placeInfos) {
            return parkPlaces;
        }

        const options = createOptions({codebookName: 'placePark', scope: eventId, asNumber: true}, {placePark: placeParkList});
        options.forEach((o) => {
            const p = placeInfos?.find((p) => p.placeId === o.value);
            // const pt = p?.placeTotals?.find((pt) => pt.dayNo === dayNo && pt.type === 'park');
            const parkPlace: ParkPlace = {
                placeId: o.value as number,
                title: p?.title || o.label || o.value + '',
                capacity: p?.parkCapacity,
                groups: []
            }

            const groupsById: { [key in number]: ParkPlaceGroup } = {};

            eventParties.forEach((ep) => {
                ep.eventDayDetails?.forEach((dd) => {
                    if (!dd.parkId) {
                        return;
                    }
                    if (dd.parkId === o.value
                        && dd.parkFlag
                        && dd.parkFlag > 0
                        && dd.dayNo
                        && FIXED_EVENT_DAY_NOS.indexOf(dd.dayNo) >= 0
                        && ep.eventDays?.[dd.dayNo - 1] === '1') {
                        const groupId = ep.groupId || FAKE_VALUE_EMPTY;
                        if (!groupsById[groupId]) {
                            const g = groupList.find((o) => o.value === '' + groupId);
                            groupsById[groupId] = {
                                groupId,
                                title: g?.label || t('Bez skupiny'),
                                days: {}
                            }
                            parkPlace.groups.push(groupsById[groupId]);
                        }
                        const g = groupsById[groupId];
                        if (!g.days[dd.dayNo]) {
                            g.days[dd.dayNo] = [];
                        }
                        g.days[dd.dayNo].push(ep);
                    }
                })
            })

            parkPlaces.push(parkPlace);
        })
        return parkPlaces;

    }, [eventId, eventParties, placeParkList, groupList, eventDays, placeInfos, t]);

    useEffect(() => {
        handleFetchItems().then();
    }, [handleFetchItems, dispatch]);

    const days = eventDays?.filter((ed) => ed.dayNo && FIXED_EVENT_DAY_NOS.indexOf(ed.dayNo) >= 0);
    const isWide = days?.length && days.length > 6;
    const isLoading = !eventDays || !placeParkList || !eventParties || !groupList || !placeInfos || !days || !techParties;
    return isLoading
        ? <LinearProgress/>
        : <Grid container spacing={2}>
            <Grid item md={isWide ? 7 : 8} sm={6}>
                <Grid container spacing={2}>
                    {parkPlaces.map((pp) => {
                        return <EventParkPlace key={pp.title} eventId={eventId} pp={pp} days={days}/>;
                    })}
                </Grid>
            </Grid>
            {!!techParties?.length && techParties.length > 0 && <Grid item md={isWide ? 5 : 4} sm={6}>
                <Card>
                    <CardHeader title={<span>{t('Technické osoby')}</span>}/>
                    <CardContent>
                        <Table className={'data-grid tech-parties'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{width: '25%'}}><span>{t('Dny')}</span></TableCell>
                                    {days?.map((d) => {
                                        return <TableCell key={d.dayNo} style={{width: (75 / days.length) + '%', textAlign: 'center'}}>
                                            <span>{dateToGuiAs(d.dayDate, "eeeee d.M.")}</span>
                                        </TableCell>;
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {techParties.map((ep) => {
                                    return <TableRow key={ep.partyId}>
                                        <TableCell><span><Link underline={'hover'}
                                            to={`/parties/${ep.partyId}/${ep.eventId}`}
                                            component={RouterLink}>{ep.companyName}</Link></span></TableCell>
                                        {days?.map((d) => {
                                            if (!d.dayNo) {
                                                return null;
                                            }
                                            let total;
                                            const dd = ep.eventDayDetails?.find((dd) => dd.dayNo === d.dayNo);
                                            if (dd?.parkCnt !== undefined) {
                                                total = dd.parkCnt;
                                            } else {
                                                total = !!dd?.parkFlag ? 1 : 0;
                                            }

                                            return <TableCell key={d.dayNo} style={{textAlign: 'center'}}><span><Link underline={'hover'}
                                                to={`/event-parties/?eventId=${eventId}&eventDay=${d.dayNo}&search=${ep.companyName}`}
                                                component={RouterLink}>{total}</Link>
                                                {total > 0 && <Chip key={'quick'} size={'small'} className={'party-tags-add'} style={{marginLeft: '2px'}}
                                                    label={'+'} title={t('Kliknutím přidat 1 kartu')}
                                                    onClick={() => {
                                                        if (d.dayNo) {
                                                            handleAddParking(ep, d.dayNo).then();
                                                        }
                                                    }}
                                                />}
                                            </span></TableCell>;
                                        })}
                                    </TableRow>;
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Grid>
            }
        </Grid>
        ;
}

export default EventReportParking;
